import { ref, uploadBytes } from "firebase/storage";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import { ImageSizes } from "@/enums/images-sizes";
import { CacheTypes } from "@/enums/cache-types";
import { fetchPicture } from "./PictureService";
import type { PictureResult } from "@/types/picture";
import imageCompression, { type Options } from "browser-image-compression";
import { handleFirestorageException } from "@/helpers/exceptions/handler";
import type { StorageError } from "firebase/storage";

// 3 months TTL for profile pictures (MD5 hash validation handles invalidation)
const PROFILE_PICTURE_TTL = 90 * 24 * 60 * 60 * 1000;

export async function getUserProfilePictureFromFirestorage(
  userUuid: string,
  format: ImageSizes = ImageSizes.SD,
  bypassCache = false,
): Promise<PictureResult> {
  const formatReference = ref(
    firebaseStorage,
    `users/${userUuid}/profile_pictures/${userUuid}${format}`,
  );
  const fallbackReference = ref(
    firebaseStorage,
    `users/${userUuid}/profile_pictures/${userUuid}`,
  );

  // When bypassing cache, set TTL to 0 to force fresh fetch but still update cache
  const ttl = bypassCache ? 0 : PROFILE_PICTURE_TTL;

  return fetchPicture({
    cacheType: CacheTypes.User,
    cacheId: userUuid,
    formatReference,
    fallbackReference,
    format,
    callerTtlMs: ttl,
    setterTtlMs: PROFILE_PICTURE_TTL,
  });
}

/**
 * Uploads a user profile picture to Firebase Storage with compression
 * @param userUuid The UUID of the user
 * @param file The image file to upload
 * @param options Compression options (optional)
 * @returns Promise that resolves when upload is complete
 * @throws StorageError if upload fails
 */
export async function uploadUserProfilePicture(
  userUuid: string,
  file: File,
  options: Options,
): Promise<void> {
  try {
    // Default compression options
    const compressionOptions: Options = {
      ...options,
      maxSizeMB: options.maxSizeMB || 1,
      maxWidthOrHeight: options.maxWidthOrHeight || 3840,
      useWebWorker: true,
    };

    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);

    // Create storage reference
    const userStorageRef = ref(
      firebaseStorage,
      `users/${userUuid}/profile_pictures/${userUuid}`,
    );

    // Upload the compressed file
    await uploadBytes(userStorageRef, compressedFile);
  } catch (error) {
    handleFirestorageException({ e: error as StorageError });
    throw error;
  }
}
